import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { filesPathFromSearch } from '../utils/filesPath';
import { addressAlreadyShows, homeViewParam } from '../utils/urlWins';

const HOME_VIEWS = ['dashboard', 'files', 'logs'];

/**
 * Keeps Project Home's view (Dashboard / Files / Logs) and the URL's `?view=` in step (Canvas.jsx).
 *
 * Reflects the Home view in the URL (?view=dashboard|files) while Project Home is shown, and drops it when a service is
 * shown instead. Does nothing until something is selected, so a ?view= in a freshly opened URL is not erased before it
 * has been applied.
 * Two things can change here, and they must not fight: the person switching the view (the URL follows it), and the URL
 * changing under us to ask for a file (`?view=files&path=...`: a link from the chat, the dashboard opening a file), in
 * which case the view follows the URL. Told apart by whether the query string just changed.
 *
 * @param {object} input
 * @param {object|null} input.selectedResource Canvas's selection (`{ type: 'configuration' }` is Project Home)
 * @param {string} input.mode the Home view shown
 * @param {(mode: string) => void} input.setMode
 * @param {boolean} input.filesLinkToService the URL's file link goes to a service's Code tab (useFilesLinkToService)
 * @param {boolean} input.serviceInUrl the route names a service
 * @param {() => void} [input.onFileLink] a file link arrived (closes any workspace view)
 * @param {() => void} [input.onFileLinkFromService] a file link arrived while a service is shown: go to Project Home
 */
export default function useHomeViewUrl({ selectedResource, mode, setMode, filesLinkToService, serviceInUrl, onFileLink, onFileLinkFromService }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const lastHomeSearchRef = useRef(location.search);
  useEffect(() => {
    if (!selectedResource) { return; }
    const atHome = selectedResource.type === 'configuration';
    const urlChanged = lastHomeSearchRef.current !== location.search;
    lastHomeSearchRef.current = location.search;
    // The link is going to its service's Code tab: Home has nothing to show or to take out of the URL.
    if (filesLinkToService) { return; }
    const asksForFile = urlChanged && Boolean(filesPathFromSearch(location.search));
    // A file link (the chat's file names) always lands on that file: it closes any workspace view (Data, Changes,
    // Activity, Sessions) and, from a service, goes to Project Home's Files view. Without this the service stayed
    // selected and the branch below stripped `view=files&path=` again, so the URL went back ~100 ms later.
    if (asksForFile) { onFileLink?.(); }
    if (asksForFile && !atHome) {
      onFileLinkFromService?.();
      setMode('files');
      return;
    }
    if (atHome && asksForFile && mode !== 'files') {
      setMode('files');
      return;
    }
    // Back / Forward: the address is what the person went back to, so Home's view follows it (from a file opened in
    // the Files view, Back is the page they came from), instead of the view being written back over it.
    const urlView = new URLSearchParams(location.search).get('view');
    if (atHome && urlChanged && navigationType === 'POP' && HOME_VIEWS.includes(urlView) && urlView !== mode) {
      setMode(urlView);
      return;
    }
    const search = new URLSearchParams(location.search);
    const current = search.get('view');
    // A URL naming a service is not Home: never add a Home `?view=` to it from remembered state (a remembered
    // Dashboard turned a link to …/services%2Fapp/main/code into …/code?view=dashboard, then …/preview).
    const wanted = homeViewParam({ atHome, serviceInUrl, mode });
    if ((wanted || null) === (current || null)) { return; }
    // The address bar is ahead of the router for a moment after a button navigates and sets the mode: keep its query.
    if (addressAlreadyShows(typeof window !== 'undefined' ? window.location.search : '', wanted)) { return; }
    if (wanted) { search.set('view', wanted); } else { search.delete('view'); }
    // A file path belongs to the Files view only, the chosen service to the Logs view only.
    if (wanted !== 'files') { search.delete('path'); search.delete('line'); search.delete('preview'); }
    if (wanted !== 'logs') { search.delete('service'); search.delete('at'); }
    const query = search.toString();
    navigate({ pathname: location.pathname, search: query ? `?${query}` : '', hash: location.hash }, { replace: true });
  }, [mode, selectedResource, location.pathname, location.search, location.hash, navigate, navigationType, filesLinkToService, serviceInUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // A link from the chat (`?view=files&path=...`) while the project is already open, or Home being reached with one in
  // the URL: show the Files view. Only then: picking Home again while it is already shown (the Home tab, from a file
  // open in the Files view) re-selects it with the file's path still in the URL for a moment, and following that path
  // here turned the click's Dashboard back into Files (the effect above then wrote `?view=files` over the
  // `?view=dashboard` it had just written, without the path), so Home took two clicks.
  const lastFollowedRef = useRef({ search: null, atHome: false });
  useEffect(() => {
    const atHome = selectedResource?.type === 'configuration';
    const previous = lastFollowedRef.current;
    lastFollowedRef.current = { search: location.search, atHome };
    if (!atHome || filesLinkToService || !filesPathFromSearch(location.search)) { return; }
    if (previous.atHome && previous.search === location.search) { return; }
    setMode('files');
  }, [location.search, selectedResource, filesLinkToService]); // eslint-disable-line react-hooks/exhaustive-deps
}
